import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import api from '../../services/api';

/**
 * Team365 → one Partner Link partner (Jenn, 2026-10-07). Its own page and
 * its own server routes (/api/admin/partners), never the affiliate screen,
 * so partner rules can change without touching affiliates. Same look as the
 * affiliate page on purpose. Shared underneath, with Jenn's OK: the page
 * builder (partners use their own "Partner page" design) and the money
 * record (payouts here land in the same books).
 */

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const day = (d) => (d ? new Date(d).toLocaleDateString() : '—');

function StatCard({ label, value, highlight }) {
  return (
    <div className={`rounded-2xl p-4 ${highlight ? 'bg-reset-terracotta/10' : 'bg-white shadow-sm'}`}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-xl font-bold text-reset-brown">{value}</p>
    </div>
  );
}

function EmailModal({ partnerId, kind, title, onClose, onSent }) {
  const [draft, setDraft] = useState(null);
  const [html, setHtml] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/admin/partners/${partnerId}/emails/${kind}/preview`)
      .then(r => { setDraft({ subject: r.data.subject, body: r.data.body, toEmail: r.data.toEmail }); setHtml(r.data.html); })
      .catch(err => setError(err.response?.data?.error || 'Could not build the preview.'));
  }, [partnerId, kind]);

  // Re-draw the real email as the text is edited (short pause so it isn't on every key).
  useEffect(() => {
    if (!draft) return undefined;
    const t = setTimeout(() => {
      api.post(`/admin/partners/${partnerId}/emails/${kind}/render`, { body: draft.body })
        .then(r => setHtml(r.data.html)).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [draft, partnerId, kind]);

  const send = async () => {
    setBusy(true); setError('');
    try {
      await api.post(`/admin/partners/${partnerId}/emails/${kind}/send`, { subject: draft.subject, body: draft.body });
      onSent();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send this email.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-lg max-w-5xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="font-serif text-lg text-reset-brownDeep mb-1">{title}</h3>
        {draft && <p className="text-xs text-gray-500 mb-4">To {draft.toEmail}. Nothing is sent until you press Send.</p>}
        {error && <p className="text-sm text-reset-terracotta mb-3">{error}</p>}
        {draft && (
          <div className="grid lg:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5" htmlFor="pe-subject">Subject</label>
              <input id="pe-subject" value={draft.subject} onChange={e => setDraft({ ...draft, subject: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3" />
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5" htmlFor="pe-body">Message</label>
              <textarea id="pe-body" value={draft.body} onChange={e => setDraft({ ...draft, body: e.target.value })} rows={18}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono" />
              <p className="text-xs text-gray-400 mt-1">**bold**, _italic_, and lines starting with "- " for bullets.</p>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">What they'll see</p>
              <iframe title="Email preview" srcDoc={html} className="w-full h-[520px] border border-gray-200 rounded-lg bg-white" />
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-sm rounded-xl text-gray-600">Cancel</button>
          <button onClick={send} disabled={!draft || busy} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl disabled:opacity-50">
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PayoutModal({ partner, owed, onClose, onSaved }) {
  const [amount, setAmount] = useState(owed > 0 ? Number(owed).toFixed(2) : '');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const how = partner.payout_method === 'paypal' ? 'PayPal' : partner.payout_method === 'etransfer' ? 'e-Transfer' : null;

  const save = async () => {
    setBusy(true); setError('');
    try {
      await api.post(`/admin/partners/${partner.id}/payouts`, { amount, payoutDate: date, note });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not record this payment.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <h3 className="font-serif text-lg text-reset-brownDeep mb-1">Record a payment to {partner.name.split(' ')[0]}</h3>
        <p className="text-sm text-gray-500 mb-4">
          {how ? <>They asked to be paid by <strong>{how}</strong> to <strong>{partner.payout_email}</strong>.</> : 'They haven\'t added their payment details yet.'}
          {' '}Recording it here marks what they're owed as paid and adds it to the books.
        </p>
        <label className="block text-sm font-medium text-reset-brown mb-1" htmlFor="po-amount">Amount</label>
        <input id="po-amount" type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3" />
        <label className="block text-sm font-medium text-reset-brown mb-1" htmlFor="po-date">Date paid</label>
        <input id="po-date" type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3" />
        <label className="block text-sm font-medium text-reset-brown mb-1" htmlFor="po-note">Note (optional)</label>
        <input id="po-note" value={note} onChange={e => setNote(e.target.value)} placeholder={how ? `${how} to ${partner.payout_email}` : ''} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3" />
        {error && <p className="text-sm text-reset-terracotta mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-sm rounded-xl text-gray-600">Cancel</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl disabled:opacity-50">{busy ? 'Saving…' : 'Record payment'}</button>
        </div>
      </div>
    </div>
  );
}

// What the partner told us on their page form, and the words that go on their
// page. Team365 writes the page words from the answers; blank = their own.
function PageWords({ partner, busy, onSave }) {
  const [story, setStory] = useState(partner.page_story_text || '');
  const [quote, setQuote] = useState(partner.page_quote_text || '');
  const answers = [
    ['What they do', partner.profession_text], ['Who they help', partner.clients_text],
    ['What their clients find hard between sessions', partner.client_needs_text], ['How the page should sound', partner.tone_text],
    ['About them and their practice', partner.story_text], ['Who they will share the page with', partner.audience_text],
    ['Why they recommend 365reset', partner.quote_text], ['Please don\'t mention', partner.do_not_mention_text],
  ].filter(([, v]) => v && v.trim());
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
      <h2 className="font-serif text-lg font-bold text-reset-brown mb-3">Their answers and page words</h2>
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mb-5">
        {answers.map(([label, value]) => (
          <div key={label}><dt className="text-xs font-medium text-gray-500">{label}</dt><dd className="text-sm text-reset-brown whitespace-pre-line">{value}</dd></div>
        ))}
      </dl>
      <label className="block mb-3">
        <span className="block text-xs font-medium text-gray-500 mb-1">Page words: about them (leave blank to use their own sentence)</span>
        <textarea value={story} onChange={e => setStory(e.target.value)} rows={4} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
      </label>
      <label className="block mb-3">
        <span className="block text-xs font-medium text-gray-500 mb-1">Page words: their quote (leave blank to use their own words)</span>
        <textarea value={quote} onChange={e => setQuote(e.target.value)} rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
      </label>
      <button disabled={busy} onClick={() => onSave({ pageStoryText: story, pageQuoteText: quote })}
        className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl disabled:opacity-50">Save page words</button>
    </div>
  );
}

export default function AdminPartnerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [email, setEmail] = useState(null);
  const [showPayout, setShowPayout] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/partners/${id}`).then(r => setData(r.data)).catch(err => setError(err.response?.data?.error || 'Could not load this partner.'));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okText) => {
    setBusy(true); setNotice(''); setError('');
    try {
      const r = await fn();
      if (r?.data?.partner) setData(r.data);
      setNotice(okText);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    }
    setBusy(false);
  };

  if (!data) {
    return <DashboardLayout><div className="max-w-5xl mx-auto"><AdminNav />{error ? <p className="text-sm text-reset-terracotta">{error}</p> : <p className="text-sm text-gray-500">Loading…</p>}</div></DashboardLayout>;
  }

  const { partner, page, templates, referred, payouts } = data;
  const how = partner.payout_method === 'paypal' ? 'PayPal' : partner.payout_method === 'etransfer' ? 'e-Transfer' : null;
  const pill = page.status === 'live'
    ? { cls: 'bg-reset-sage/10 text-reset-sage', text: '● Their page and link are live' }
    : page.status === 'waiting'
      ? { cls: 'bg-reset-terracotta/10 text-reset-terracotta', text: '● Page form sent in — waiting on you to review & approve' }
      : { cls: 'bg-reset-gold/25 text-reset-brown', text: '● Page form not sent in yet' };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        <AdminNav />
        <Link to="/admin/affiliate-manager#partners" className="text-xs text-gray-400 hover:text-reset-terracotta">&larr; All partners</Link>

        <div className="flex items-center justify-between mt-2 mb-1 flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-serif text-2xl font-bold text-reset-brown">{partner.name}</h1>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-reset-sage/10 text-reset-sage">Partner Link</span>
            {partner.is_active === false && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Archived</span>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setForm({ name: partner.name, email: partner.email, practiceName: partner.practice_name || '', websiteUrl: partner.website_url || '' }); setEditing(v => !v); }}
              className="px-3 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-600 hover:border-reset-terracotta hover:text-reset-terracotta">{editing ? 'Cancel' : 'Edit'}</button>
            <button disabled={busy} onClick={() => act(() => api.put(`/admin/partners/${id}`, { isActive: partner.is_active === false }), partner.is_active === false ? 'Reactivated.' : 'Archived. Their link and page are off.')}
              className="px-3 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-600 hover:border-red-300 hover:text-red-600 disabled:opacity-50">{partner.is_active === false ? 'Reactivate' : 'Archive'}</button>
            <button onClick={() => setShowPayout(true)} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown">Record payout</button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-1">{partner.email}{partner.practice_name ? ` · ${partner.practice_name}` : ''}{partner.website_url ? ' · ' : ''}{partner.website_url && <a href={partner.website_url} target="_blank" rel="noopener noreferrer" className="text-reset-terracotta underline">website</a>}</p>
        <p className="text-xs bg-reset-cream inline-block px-3 py-1 rounded-full text-gray-500 mb-4">
          Partners see totals, their link, and the names of only those clients who agreed to share. Everything else on this page is Team365-only.
        </p>

        {notice && <p className="text-sm bg-reset-sage/10 text-reset-sage rounded-lg px-3 py-2 mb-3">{notice}</p>}
        {error && <p className="text-sm bg-reset-terracotta/10 text-reset-terracotta rounded-lg px-3 py-2 mb-3">{error}</p>}

        {editing && (
          <div className="bg-white rounded-2xl shadow-sm p-5 mb-6 grid sm:grid-cols-2 gap-3">
            {[['name', 'Name'], ['email', 'Email'], ['practiceName', 'Practice name'], ['websiteUrl', 'Website']].map(([k, label]) => (
              <label key={k} className="text-sm text-reset-brown">{label}
                <input value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              </label>
            ))}
            <div className="sm:col-span-2">
              <button disabled={busy} onClick={async () => { await act(() => api.put(`/admin/partners/${id}`, form), 'Saved.'); setEditing(false); }}
                className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl disabled:opacity-50">Save</button>
            </div>
          </div>
        )}

        {/* Their answers + the page words Team365 writes from them (Jenn, 2026-10-09) */}
        {partner.profile_submitted_at && <PageWords partner={partner} busy={busy} onSave={(body) => act(() => api.put(`/admin/partners/${id}`, body), 'Page words saved. Preview their page to see them.')} />}

        {/* Their page */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
          <div className={`inline-flex items-center text-xs font-medium px-3 py-1.5 rounded-full mb-3 ${pill.cls}`}>{pill.text}</div>
          {page.link && <p className="text-sm mb-3">Their link: <a href={page.link} target="_blank" rel="noopener noreferrer" className="text-reset-terracotta underline break-all">{page.link}</a></p>}
          <div className="flex flex-wrap gap-2 mb-4">
            {page.status === 'not_started' && (
              <button onClick={() => setEmail({ kind: 'intake', title: 'Send their page form email' })} className="px-3 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl">Send page form email</button>
            )}
            {page.landing_preview_url && <a href={page.landing_preview_url} target="_blank" rel="noopener noreferrer" className="px-3 py-2 border border-gray-200 text-sm rounded-xl text-reset-brown">Preview their page</a>}
            {page.confirmation_preview_url && <a href={page.confirmation_preview_url} target="_blank" rel="noopener noreferrer" className="px-3 py-2 border border-gray-200 text-sm rounded-xl text-reset-brown">Preview their welcome page</a>}
            {page.status === 'waiting' && (
              <button disabled={busy} onClick={() => act(() => api.post(`/admin/partners/${id}/approve-page`), 'Approved. Their page and link are live. You can send them the "page is live" email now.')}
                className="px-3 py-2 bg-reset-sage text-white text-sm font-medium rounded-xl disabled:opacity-50">Approve page</button>
            )}
            {page.status === 'live' && (
              <>
                <button onClick={() => setEmail({ kind: 'approval', title: 'Send the "your page is live" email' })} className="px-3 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl">Send "page is live" email</button>
                <button disabled={busy} onClick={() => act(() => api.post(`/admin/partners/${id}/unapprove-page`), 'Taken offline. Their link and page are off until you approve again.')}
                  className="px-3 py-2 border border-gray-200 text-sm rounded-xl text-gray-600 disabled:opacity-50">Take offline</button>
              </>
            )}
            {page.status !== 'not_started' && (
              <button onClick={() => setEmail({ kind: 'intake', title: 'Send their page form link again' })} className="px-3 py-2 border border-gray-200 text-sm rounded-xl text-gray-600">Send form link again</button>
            )}
          </div>
          <label className="text-sm text-reset-brown">Page design{' '}
            <select value={partner.landing_template_id || ''} disabled={busy}
              onChange={e => act(() => api.put(`/admin/partners/${id}`, { landingTemplateId: e.target.value || null }), 'Page design saved.')}
              className="ml-2 border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
              <option value="">Default design</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <p className="text-xs text-gray-400 mt-1">Designs are edited in Content Library → Templates.</p>
        </div>

        {/* Their choices */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
          <h2 className="text-sm font-bold text-reset-brown mb-2">Their 10%</h2>
          <p className="text-sm text-reset-brown">
            {partner.give_to_clients
              ? 'Gives it to their clients: people who join through their link get 10% off every payment, and nothing is owed to them for those people.'
              : how ? <>Keeps it. Paid monthly by <strong>{how}</strong> to <strong>{partner.payout_email}</strong>{partner.in_canada === false ? ' (outside Canada)' : ''}.</>
                : 'Keeps it, but hasn\'t added their payment details on their dashboard yet.'}
          </p>
          <p className="text-xs text-gray-400 mt-1">They choose this themselves on their partner dashboard. Link code: <span className="font-mono">{partner.referral_code}</span></p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
          <StatCard label="Owed" value={money(data.owed)} highlight />
          <StatCard label="Paid to date" value={money(data.paid)} />
          <StatCard label="Joined through them" value={referred.length} />
        </div>

        <h2 className="text-sm font-bold text-reset-brown mb-3">People who joined through them</h2>
        {referred.length === 0 ? (
          <p className="text-sm text-gray-400 mb-8">No one yet.</p>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50 mb-8">
            {referred.map(m => (
              <Link key={m.id} to={`/admin/members/${m.id}`} className="px-5 py-3 flex flex-wrap items-center justify-between gap-2 text-sm hover:bg-reset-cream/40">
                <span className="text-reset-brown font-medium">{m.name}{!m.is_active && <span className="text-xs text-gray-400"> (no longer a member)</span>}</span>
                <span className="text-xs text-gray-500">
                  Joined {day(m.member_since)}
                  {m.client_discount ? ' · 10% client discount' : ''}
                  {' · '}{m.shares_summaries ? 'shares summaries (partner sees their name)' : 'not sharing (partner sees a placeholder)'}
                </span>
              </Link>
            ))}
          </div>
        )}

        <h2 className="text-sm font-bold text-reset-brown mb-3">Payments to them</h2>
        {payouts.length === 0 ? (
          <p className="text-sm text-gray-400 mb-8">None yet.</p>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-50 mb-8">
            {payouts.map(p => (
              <div key={p.id} className="px-5 py-3 flex justify-between text-sm">
                <span className="text-reset-brown">{day(p.payout_date)}{p.note ? <span className="text-gray-400"> · {p.note}</span> : null}</span>
                <span className="font-medium text-reset-brown">{money(p.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {email && (
        <EmailModal partnerId={id} kind={email.kind} title={email.title}
          onClose={() => setEmail(null)}
          onSent={() => { setEmail(null); setNotice('Email sent.'); }} />
      )}
      {showPayout && (
        <PayoutModal partner={partner} owed={data.owed}
          onClose={() => setShowPayout(false)}
          onSaved={() => { setShowPayout(false); setNotice('Payment recorded.'); load(); }} />
      )}
    </DashboardLayout>
  );
}
